import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PlayingCard } from "../PlayingCard";
import { t, useLang } from "../../lib/i18n";

const hasPreview = (p) =>
  (!p.has_played_nemna3 && p.preview_state?.groups?.length > 0) || p.preview_state?.additions?.length > 0;

/**
 * The pose another player is composing (their `preview_state`, sent by the server while they compose it),
 * centred at the top of the table with their name (owner's request). It sat under that player's pill in the
 * opponents' row, where it widened their column and pushed the other pills aside with 3 or 4 players.
 * Groups are left out once the player has posed (already on the table); cards added to groups on the table are
 * counted (TableCombinations also marks those groups).
 */
export function PosePreview({ players, myId, playerColorMap }) {
  useLang();
  const p = (players || []).find((x) => x.user_id !== myId && !x.eliminated && hasPreview(x));
  const groups = p && !p.has_played_nemna3 ? p.preview_state.groups || [] : [];
  const added = p ? (p.preview_state.additions || []).reduce((s, a) => s + (a.cards?.length ?? 0), 0) : 0;
  const color = p && playerColorMap?.[p.user_id];

  return (
    <AnimatePresence>
      {p && (
        <motion.div
          key={`pose-preview-${p.user_id}`}
          initial={{ opacity: 0, y: -6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
          className="mb-1.5 max-w-[min(94%,340px)] rounded-lg border border-amber-400/50 bg-slate-950/85 px-2 py-1 shadow-lg shadow-black/40"
          data-testid="pose-preview"
        >
          <div className="flex items-center justify-center gap-1.5 text-[10px] font-semibold text-amber-300">
            <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: color?.hex ?? "#fcd34d" }} />
            <span className="truncate">
              {p.preview_state.mode === "nefrech"
                ? t("opp.nefrechInProgress", { name: p.name })
                : t("opp.preparing", { name: p.name })}
            </span>
          </div>
          {groups.length > 0 && (
            // Each group in a frame of its own (owner's request): side by side, their cards ran together.
            <div className="mt-1 flex flex-wrap justify-center gap-1.5" dir="ltr">
              {groups.map((g, idx) => (
                <div key={idx} className="rounded-md border border-amber-300/45 bg-white/[0.07] p-1">
                  <div className="card-fan">
                    {g.cards.map((c) => (
                      <div key={c.id} className={`leading-[0] ${c.is_joker ? "card-fan-whole" : ""}`}>
                        <PlayingCard card={c} size="xs" disabled readOnly />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
          {added > 0 && (
            <div className={`text-center text-[8px] text-amber-400/80 ${groups.length > 0 ? "mt-0.5 border-t border-amber-400/15 pt-0.5" : ""}`}>
              {t("opp.addsExisting", { n: added })}
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default PosePreview;
